import { z } from "zod/v4";

const DEFAULT_REQUIRED_TEXT = "This field is required";

interface CreateZodEnumProps<T extends Record<string, string>> {
  valueObject: T;
  allowEmptyString?: boolean;
  customErrorText?: string;
}

// Creates Zod enums from string records
// Example: {PINK: "#FFAABB", BLACK: "#000000"} -> z.enum(["#FFAABB", "#000000"])
export const createZodEnums = <T extends Record<string, string>>({
  valueObject,
  allowEmptyString = false,
  customErrorText = DEFAULT_REQUIRED_TEXT,
}: CreateZodEnumProps<T>) => {
  const values = ["", ...Object.values(valueObject)] as [
    "", // Adding the empty string allows us to have empty default values
    string, // this ensures we have at least one non empty value in our array
    ...string[], // this allows for an arbitrary number of other string values
  ];

  let schema = z.enum(values);

  if (allowEmptyString) {
    // This escape hatch allows empty string values to be valid
    return schema;
  }

  // By default we check that an empty string is not submitted during validation
  return schema.refine((val) => val !== "", customErrorText);
};

// When we know we only have string values
export const createZodStringOption = (customErrorText?: string) =>
  z.object(
    {
      label: z.string(),
      value: z.string().min(1, DEFAULT_REQUIRED_TEXT),
    },
    {
      error: customErrorText || DEFAULT_REQUIRED_TEXT,
    },
  );

// When we know we only have number values
export const createZodNumberOption = (customErrorText?: string) =>
  z.object(
    {
      label: z.string(),
      value: z.number(),
    },
    {
      error: customErrorText || DEFAULT_REQUIRED_TEXT,
    },
  );

// More flexible version that allows for both string and number values
export const OptionSchema = z
  .object({
    label: z.string(),
    value: z.string().min(1, DEFAULT_REQUIRED_TEXT).or(z.number()),
  })
  .refine((val) => {
    return val !== undefined;
  }, DEFAULT_REQUIRED_TEXT);

export const BoolAsString = z.preprocess(
  (val) => typeof val === "boolean" && val.toString(),
  z.enum(["true", "false"]),
);
